import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_CONTACT_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { BasicCustomerDataResponse, ContactDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { validateEmail, validateName, validatePersonalNumber, validatePhone } from '../../utils/contactValidationSchemas';
import ErrorDisplay from '../elements/ErrorDisplay';
import FetchingAnimation from '../elements/FetchingAnimation';

import { getMyAccountMenuItems } from '../../constants/menuItems';
import { useAddUserProfile } from '../../hooks/apolloHooks/useAddUserProfile';
import { useDeleteUserProfile } from '../../hooks/apolloHooks/useDeleteUserProfile';
import { useGetAllCards } from '../../hooks/apolloHooks/useGetAllCards';
import { useGetAllUserProfileRoles } from '../../hooks/apolloHooks/useGetAllUserProfileRoles';
import { useGetAllUserProfiles } from '../../hooks/apolloHooks/useGetAllUserProfiles';
import { useGetOwnerCustomerRoles } from '../../hooks/apolloHooks/useGetOwnerCustomerRoles';
import { useUpdateUserProfile } from '../../hooks/apolloHooks/useUpdateUserProfile';
import { useUpdateUserProfileRoles } from '../../hooks/apolloHooks/useUpdateUserProfileRoles';
import useCountUp from '../../hooks/useCountUp';
import Button from '../elements/Button';
import RightsWrapper from '../elements/RightsWrapper';
import SecondMenu from '../elements/SecondMenu';
import Tooltip from '../elements/Tooltip';
import { AccountUpdateIcon, InfoIconOutlined, PlusIcon, SuccessIcon } from '../svgs/ExpressonSVGs';
import UserManagementTableRow from '../views/UserManagement/UserManagementTableRow';
type UserManagementProps = { data?: any }

interface structuredUserRoles {
	other: { roleName: string, displayName: string, groupingName: string, description: string, isApiEditable: boolean }[],
	write: { roleName: string, displayName: string, groupingName: string, description: string, isApiEditable: boolean }[],
	read: { roleName: string, displayName: string, groupingName: string, description: string, isApiEditable: boolean }[],
}

type rolesRadioOption = 'full' | 'read'

const UserManagementPage: React.FC<UserManagementProps> = () => {
	const userManagementWrapperClasses = 'white-pale-section shadow-one';
	const dialogNewUserRef = React.useRef<HTMLDialogElement>(null);
	const dialogRemoveUserRef = React.useRef<HTMLDialogElement>(null);
	const { cards, isComplete } = useGetAllCards()
	const cardTypes = new Set(cards.map(card => card.type.shortName))
	const [selectedRole, setSelectedRole] = React.useState<rolesRadioOption>('full')
	const [acceptedConditions, setAcceptedConditions] = useState(false);
	// This state is to control whether the new user dialog is in "add" mode or "edit" mode. In edit mode, the form will be pre-filled and the submit handler will update an existing user instead of creating a new one. For now we only have add user implemented, but this state will be useful when we implement edit user.
	const [isEditMode, setIsEditMode] = useState(false);

	// New user form state
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [personalNumber, setPersonalNumber] = useState('');
	const [firstNameError, setFirstNameError] = useState<string | undefined>();
	const [lastNameError, setLastNameError] = useState<string | undefined>();
	const [emailError, setEmailError] = useState<string | undefined>();
	const [phoneError, setPhoneError] = useState<string | undefined>();
	const [personalNumberError, setPersonalNumberError] = useState<string | undefined>();
	const [submitError, setSubmitError] = useState<string | undefined>();
	const [addUserSuccess, setAddUserSuccess] = useState(false);
	const [wasEditMode, setWasEditMode] = useState(false);
	const [pendingDeleteProfile, setPendingDeleteProfile] = useState<{ id: number; firstName: string; lastName: string; identifier: string } | null>(null);
	const [deleteError, setDeleteError] = useState<string | undefined>();
	const [pendingEditProfile, setPendingEditProfile] = useState<{ id: number; firstName: string; lastName: string; email: string; phone: string; identifier: string; rights: string } | null>(null);
	const [isFormDirty, setIsFormDirty] = useState(false);

	const { addUserProfile, loading: addUserProfileLoading } = useAddUserProfile();
	const { updateUserProfile, loading: updateUserProfileLoading } = useUpdateUserProfile();
	const { updateUserProfileRoles } = useUpdateUserProfileRoles();
	const { deleteUserProfile, loading: deleteUserProfileLoading } = useDeleteUserProfile();

	// Check which card types customer has which is synonym to if they have invoice and statements services
	let hasP = cardTypes.has('P')  // Smartkort
	let hasK = cardTypes.has('K')  // Tankkort/Företagskort
	const menuItems = getMyAccountMenuItems(hasP, hasK);

	// Always fetch data - no props needed
	const { data: customerBasicData, loading: customerLoading, error: customerError } = useGetCustomer<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		undefined,
		{ fetchPolicy: 'network-only' }
	);

	const { data: customerContactData, loading: customerContactLoading, error: customerContactError, refetch: refetchContactData } = useGetCustomer<ContactDataResponse>(
		GET_CONTACT_CUSTOMER_DATA_QUERY,
		undefined,
		{ fetchPolicy: 'network-only' }
	);

	// Get the available roles from allkort.
	const { roles, loading: rolesLoading, error: rolesError, isComplete: rolesComplete } = useGetOwnerCustomerRoles();
	const structuredRoles = useMemo<structuredUserRoles>(() => {
		const result: structuredUserRoles = { other: [], write: [], read: [] };
		if (!rolesComplete || rolesLoading || rolesError) return result;
		roles.forEach((role) => {
			const entry = {
				roleName: role.claimName,
				displayName: role.displayName ?? '',
				groupingName: role.groupingName ?? '',
				description: role.description ?? '',
				isApiEditable: role.isApiEditable,
			};
			if (role.claimName.includes('query') || role.claimName.includes('Read')) {
				result.read.push(entry);
			} else if (role.claimName.includes('mutation') || role.claimName.includes('Write')) {
				result.write.push(entry);
			} else {
				result.other.push(entry);
			}
		});
		return result;
	}, [roles, rolesComplete, rolesLoading, rolesError]);

	const { userProfiles, loading: profilesLoading, error: profilesError, isComplete: profilesComplete, refetch: refetchUserProfiles } = useGetAllUserProfiles();
	const { profileRoles, loading: profileRolesLoading, error: profileRolesError, isComplete: profileRolesComplete, refetch: refetchProfileRoles } = useGetAllUserProfileRoles();

	const ownerId = customerBasicData?.currentCustomer?.id?.toString();

	const profilesWithRoles = userProfiles
		.map(p => ({
			...p,
			roles: profileRoles.find(r => r.id === p.id)?.roles ?? [],
		}))
		.filter(p => p.identifier !== ownerId);

	const isDuplicateIdentifier = profilesWithRoles.some(p =>
		p.identifier === personalNumber && p.id !== pendingEditProfile?.id
	);

	const isFormValid =
		validateName(firstName).valid &&
		validateName(lastName).valid &&
		validateEmail(email).valid &&
		validatePhone(phone).valid &&
		validatePersonalNumber(personalNumber).valid &&
		!isDuplicateIdentifier;


	// Console log errors
	if (customerError) console.error('Failed to load customer data:', customerError.message)
	if (customerContactError) console.error('Failed to load customer contact data:', customerContactError.message)
	if (rolesError) console.error('Failed to load customer roles:', rolesError.message)
	if (profilesError) console.error('Failed to load user profiles:', profilesError.message)
	if (profileRolesError) console.error('Failed to load user profile roles:', profileRolesError.message)

	const isCompany = customerBasicData?.currentCustomer?.isCompany || false;
	const isFrozen = customerBasicData?.currentCustomer?.frozen.is || false;

	const getRightsText = (profileRoles: { claimName: string }[]): string => {
		if (profileRoles.length === 0) return '-';
		const otherNames = new Set(structuredRoles.other.map(r => r.roleName));
		const adminOnlyWrites = new Set(['mutation.updateCurrentCustomer.email', 'mutation.updateCurrentCustomer.phoneNumber']);
		const legacyAdminWrites = new Set(['MainEmailWrite', 'MainPhoneWrite']); // Legacy admin-only permissions
		const regularWrites = structuredRoles.write.filter(r => !adminOnlyWrites.has(r.roleName) && !legacyAdminWrites.has(r.roleName)).map(r => r.roleName);
		const requiredReadNames = structuredRoles.read.map(r => r.roleName);
		const profileClaimSet = new Set(profileRoles.map(r => r.claimName));

		// Admin: has 'other' role OR has any admin-only write permission (new or legacy)
		const hasAdmin = profileRoles.some(r => otherNames.has(r.claimName)) ||
			profileRoles.some(r => adminOnlyWrites.has(r.claimName) || legacyAdminWrites.has(r.claimName));

		// Redaktör: has all reads AND all regular writes (excluding admin-only writes)
		const hasAllReads = requiredReadNames.length > 0 && requiredReadNames.every(name => profileClaimSet.has(name));
		const hasAllRegularWrites = regularWrites.length > 0 && regularWrites.every(name => profileClaimSet.has(name));
		const hasAnyWrite = structuredRoles.write.some(r => profileClaimSet.has(r.roleName));

		if (hasAdmin) return 'Administratör';
		if (hasAllReads && hasAllRegularWrites) return 'Redaktör';
		if (hasAnyWrite) return '-'; // partial writes — not a valid role
		if (hasAllReads) return 'Läsare';
		return '-';
	};

	// Staggered count-up animation for the overview section
	const adminCount = profilesWithRoles.filter(p => getRightsText(p.roles) === 'Administratör').length;
	const editorCount = profilesWithRoles.filter(p => getRightsText(p.roles) === 'Redaktör').length;
	const readerCount = profilesWithRoles.filter(p => getRightsText(p.roles) === 'Läsare').length;
	const [adminEnabled, setAdminEnabled] = useState(false);
	const [editorEnabled, setEditorEnabled] = useState(false);
	const [readerEnabled, setReaderEnabled] = useState(false);
	useEffect(() => {
		setAdminEnabled(true);
		const t1 = setTimeout(() => setEditorEnabled(true), 1500);
		const t2 = setTimeout(() => setReaderEnabled(true), 3000);
		return () => { clearTimeout(t1); clearTimeout(t2); };
	}, []);
	const adminCountAnimated = useCountUp(adminCount, 800, adminEnabled);
	const editorCountAnimated = useCountUp(editorCount, 800, editorEnabled);
	const readerCountAnimated = useCountUp(readerCount, 800, readerEnabled);

	if (customerLoading || customerContactLoading) {
		return (
			<div className="skeleton-user-management-page">
				{/* this anumates the secondmenu component */}
				<FetchingAnimation width={644} maxWidth={true} height={151} marginBottom={14} marginTop={0} />
				<div className="skeleton-page-content-wrapper">
					<FetchingAnimation width={'100'} maxWidth={true} height={600} marginBottom={10} marginTop={15} />
				</div>
			</div>);
	}

	if (customerError || customerContactError) {
		return (
			<>
				{customerError && <ErrorDisplay errorContext="Fel vid laddning av kunddata:" errorMessage={customerError.message} />}
				{customerContactError && <ErrorDisplay errorContext="Fel vid laddning av kontaktuppgifter:" errorMessage={customerContactError.message} />}
			</>
		);
	}

	// Handler for submitting the new user form
	const handleAddUserSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setSubmitError(undefined);
		try {
			const newProfile = await addUserProfile({ email, firstName, identifier: personalNumber, lastName, phoneNumber: phone });
			// Right after creating the user profile, assign the selected role
			if (newProfile) {
				// Read roles use query/Read claims; editable roles use mutation/Write claims.
				const isReadRole = (claimName: string) => claimName.includes('query') || claimName.includes('Read');
				const isWriteRole = (claimName: string) => claimName.includes('mutation') || claimName.includes('Write');
				const adminOnlyWrites = new Set(['mutation.updateCurrentCustomer.email', 'mutation.updateCurrentCustomer.phoneNumber', 'MainEmailWrite', 'MainPhoneWrite']);
				const rolesToAdd = selectedRole === 'full'
					? roles.filter(r => (isReadRole(r.claimName) || isWriteRole(r.claimName)) && !adminOnlyWrites.has(r.claimName)).map(r => r.claimName)
					: roles.filter(r => isReadRole(r.claimName)).map(r => r.claimName);
				await updateUserProfileRoles({ userProfileId: newProfile.id, rolesToAdd, rolesToRemove: [] });
			}
			setFirstName('');
			setLastName('');
			setEmail('');
			setPhone('');
			setPersonalNumber('');
			setWasEditMode(false);
			setAddUserSuccess(true);
		} catch (err: any) {
			setSubmitError(err?.message ?? 'Ett fel inträffade. Försök igen.');
		}
	};

	// Handler for submitting the edit user form
	const handleUpdateUserSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!pendingEditProfile) return;
		setSubmitError(undefined);
		try {
			// Update profile fields
			await updateUserProfile({
				userProfileId: pendingEditProfile.id,
				email,
				firstName,
				lastName,
				phoneNumber: phone,
			});

			// Update roles only for non-admins
			const isAdmin = pendingEditProfile.rights === 'Administratör';
			if (!isAdmin) {
				const originalRole = pendingEditProfile.rights === 'Redaktör' ? 'full' : 'read';
				// Also force cleanup when rights are '-' (faulty/partial roles), regardless of selected role
				const hasFaultyRoles = pendingEditProfile.rights === '-';
				if (selectedRole !== originalRole || hasFaultyRoles) {
					const isReadRole = (claimName: string) => claimName.includes('query') || claimName.includes('Read');
					const isWriteRole = (claimName: string) => claimName.includes('mutation') || claimName.includes('Write');
					const adminOnlyWrites = new Set(['mutation.updateCurrentCustomer.email', 'mutation.updateCurrentCustomer.phoneNumber', 'MainEmailWrite', 'MainPhoneWrite']);
					if (selectedRole === 'full') {
						// Upgrading to Redaktör: add write roles except admin-only (read roles already assigned)
						const rolesToAdd = roles.filter(r => isWriteRole(r.claimName) && !adminOnlyWrites.has(r.claimName)).map(r => r.claimName);
						await updateUserProfileRoles({ userProfileId: pendingEditProfile.id, rolesToAdd, rolesToRemove: [] });
					} else {
						// Downgrading to Läsare (or fixing faulty roles): remove all write roles, ensure all read roles assigned
						const rolesToRemove = roles.filter(r => isWriteRole(r.claimName)).map(r => r.claimName);
						const rolesToAdd = roles.filter(r => isReadRole(r.claimName)).map(r => r.claimName);
						await updateUserProfileRoles({ userProfileId: pendingEditProfile.id, rolesToAdd, rolesToRemove });
					}
				}
			}

			setWasEditMode(true);
			setAddUserSuccess(true);
		} catch (err: any) {
			setSubmitError(err?.message ?? 'Ett fel inträffade. Försök igen.');
		}
	};

	const handleDeleteUserConfirm = async () => {
		if (!pendingDeleteProfile) return;
		setDeleteError(undefined);
		try {
			await deleteUserProfile(pendingDeleteProfile.id);
			refetchUserProfiles();
			refetchProfileRoles();
			setPendingDeleteProfile(null);
			dialogRemoveUserRef.current?.close();
		} catch (err: any) {
			setDeleteError(err?.message ?? 'Ett fel inträffade. Försök igen.');
		}
	};

	return (
		<>
			<SecondMenu menuItems={menuItems} />
			<div className="Section user-management-page-component">
				<div className="Row Row--JustifyLeft">
					{/* Header h1 and info message */}
					<div
						className={classNames(
							'Row__Inner',
							'user-management-header',
							'shadow-one',
							'white-pale-section'
						)}

					>
						<div className="header-content">
							<h1>Hantera användare</h1>
							<p>
								Ge andra tillgång till ditt konto så att de kan hjälpa dig att hantera eller visa information om dina kort och tjänster.
							</p>
						</div>
						<div className="user-management-overview has-dark-bg">
							<div>
								<div>
									<span className="user-management-overview-title">
										Administratörer
									</span>
									<span className="count">
										{Math.round(adminCountAnimated)}
									</span>
								</div>
								<div>
									<span className="user-management-overview-title">
										Redaktörer
									</span>
									<span className="count">
										{Math.round(editorCountAnimated)}
									</span>
								</div>
								<div>
									<span className="user-management-overview-title">
										Läsare
									</span>
									<span className="count">
										{Math.round(readerCountAnimated)}
									</span>
								</div>
								<Tooltip position="top-end"
									text={<>
										<p><strong>Administratörer:</strong> har fullständiga rättigheter.</p>
										<p><strong>Redaktörer:</strong> kan visa och redigera information.</p>
										<p><strong>Läsare:</strong> kan endast visa information.</p>
									</>}
								>
									<InfoIconOutlined width={25} height={26} />
								</Tooltip>
							</div>

						</div>
					</div>

					<div className={classNames(
						'user-management-content-wrapper',
						'Row__Inner',
						'shadow-one',
						'white-pale-section'
					)
					}>
						<div className="user-management-content-header">
							<span >
								{profilesWithRoles.length === 1 ?
									'Endast du har tillgång till kontot' :
									`${profilesWithRoles.length} användare har tillgång till kontot`}
							</span>

							{/* This dialog handles the new user addition and edit user */}
							<dialog
								ref={dialogNewUserRef}
								className={classNames(
									'dialog-new-user',
									{
										'edit-mode': isEditMode,
									}
								)}
							>
								<div className="dialog-content">
									<h2 className="title body-text">
										<span className="svg-icon-wrapper blue round">
											{addUserSuccess ? <SuccessIcon width={28} height={28} /> : <AccountUpdateIcon width={28} height={28} />}
										</span>
										{addUserSuccess ? (wasEditMode ? 'Användare uppdaterad' : 'Användare tillagd') : isEditMode ? 'Ändra användarinformation' : 'Lägg till användare'}
									</h2>

									{addUserSuccess ? (
										<>
											<p>
												{wasEditMode
													? 'Användarens uppgifter har uppdaterats.'
													: 'Användaren har lagts till och kan nu logga in med BankID kopplat till det angivna personnumret.'
												}
											</p>
											<div className="dialog-actions one-column">
												<Button type="button" className="button-confirm body-text" onClick={() => {
													setAddUserSuccess(false);
													setWasEditMode(false);
													setSelectedRole('full');
													refetchUserProfiles();
													refetchProfileRoles();
													dialogNewUserRef.current?.close();
												}}>Stäng</Button>
											</div>
										</>
									) : (
										<>
											{isEditMode ? (
												<p>Ändra uppgifterna för användaren nedan. Klicka på Spara för att bekräfta ändringarna.</p>
											) : (
												<p>
													Användare som får tillgång kan antingen få redaktörsrättigheter eller endast visningsrättigheter.
													De loggar in med BankID som är kopplat till personummret som angetts.
												</p>
											)}
											<form method="dialog" onSubmit={isEditMode ? handleUpdateUserSubmit : handleAddUserSubmit}>

												<div className="input-wrapper">
													<span>Förnamn<span className="red-text">*</span></span>
													<input
														type="text"
														id="firstName"
														name="firstName"
														placeholder="Förnamn"
														value={firstName}
														onChange={e => { setFirstName(e.target.value); setIsFormDirty(true); }}
														onBlur={() => { const r = validateName(firstName); setFirstNameError(r.error); }}
													/>
													{firstNameError && <span className="input-error">{firstNameError}</span>}
												</div>

												<div className="input-wrapper">
													<span>Efternamn<span className="red-text">*</span></span>
													<input
														type="text"
														id="lastName"
														name="lastName"
														placeholder="Efternamn"
														value={lastName}
														onChange={e => { setLastName(e.target.value); setIsFormDirty(true); }}
														onBlur={() => { const r = validateName(lastName); setLastNameError(r.error); }}
													/>
													{lastNameError && <span className="input-error">{lastNameError}</span>}
												</div>

												<div className="input-wrapper one-column">
													<span>E-post<span className="red-text">*</span></span>
													<input
														type="email"
														id="email"
														name="email"
														placeholder="Ange e-postadress"
														value={email}
														onChange={e => { setEmail(e.target.value); setIsFormDirty(true); }}
														onBlur={() => { const r = validateEmail(email); setEmailError(r.error); }}
													/>
													{emailError && <span className="input-error">{emailError}</span>}
												</div>

												<div className="input-wrapper">
													<span>Telefonnummer<span className="red-text">*</span></span>
													<input
														type="tel"
														id="phone"
														name="phone"
														placeholder="+46701234567"
														value={phone}
														onChange={e => { setPhone(e.target.value); setIsFormDirty(true); }}
														onBlur={() => { const r = validatePhone(phone); setPhoneError(r.error); }}
													/>
													{phoneError && <span className="input-error">{phoneError}</span>}
												</div>

												<div className="input-wrapper">
													<span>Personnummer<span className="red-text">*</span></span>
													<input
														type="text"
														id="personalNumber"
														name="personalNumber"
														placeholder="12 siffror utan bindestreck"
														value={personalNumber}
														disabled={isEditMode}
														onChange={e => { setPersonalNumber(e.target.value.replace(/[^\d]/g, '')); setIsFormDirty(true); }}
														onBlur={() => {
															const r = validatePersonalNumber(personalNumber);
															if (!r.valid) { setPersonalNumberError(r.error); return; }
															if (isDuplicateIdentifier) { setPersonalNumberError('Det finns redan en användare med detta personnummer.'); return; }
															setPersonalNumberError(undefined);
														}}
													/>
													{personalNumberError && <span className="input-error">{personalNumberError}</span>}
												</div>
												{(isEditMode && pendingEditProfile?.rights === 'Administratör') && (
													<div className="input-wrapper info-message one-column">
														<span>
															Denna användare har administratörsrättigheter, vilket innebär att de redan har full tillgång till kontot. Rättighetsinställningarna kan inte ändras för administratörer.
														</span>
													</div>
												)}
												{!(isEditMode && pendingEditProfile?.rights === 'Administratör') && (
													<div className="input-wrapper radio-buttons-wrapper one-column">
														<span>
															Välj rättigheter<span className="red-text">*</span>
														</span>

														<div className="radio-buttons">
															<label
																tabIndex={0}
																onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedRole('full'); } }}
															>
																<input
																	type="radio"
																	name="role"
																	value="full"
																	checked={selectedRole === 'full'}
																	tabIndex={-1}
																	onChange={(e) => setSelectedRole(e.target.value as rolesRadioOption)}
																/>
																Redaktör
																<Tooltip position='top' text={`Kan se all information och utföra alla åtgärder, inklusive att lägga till och ta bort användare.`}>
																	<InfoIconOutlined width={20} height={20} />
																</Tooltip>
															</label>

															<label
																tabIndex={0}
																onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedRole('read'); } }}
															>
																<input
																	type="radio"
																	name="role"
																	value="read"
																	checked={selectedRole === 'read'}
																	tabIndex={-1}
																	onChange={(e) => setSelectedRole(e.target.value as rolesRadioOption)}
																/>
																Läsare
																<Tooltip position='top-start' text={`Kan endast se information, utan möjlighet att göra ändringar eller utföra åtgärder.`}>
																	<InfoIconOutlined width={20} height={20} />
																</Tooltip>
															</label>
														</div>
													</div>
												)}

												<div className="input-wrapper one-column">
													<label className="checkbox-label">
														<input
															type="checkbox"
															name="notifyUser"
															checked={acceptedConditions}
															onChange={() => setAcceptedConditions(!acceptedConditions)}
															onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setAcceptedConditions(!acceptedConditions); } }}
														/>
														{isEditMode ? (
															<>
																Med dessa ändringar godkänner du <a target="_blank" href="https://qstar.se/villkor-for-behorighetstilldelning">Villkor för behörighetstilldelning</a>.
															</>
														) : (
															<>
																Kunden utser ovan angiven person till användare enligt <a target="_blank" href="https://qstar.se/villkor-for-behorighetstilldelning">Villkor för behörighetstilldelning</a> och godkänner härmed dessa villkor.
															</>
														)}

													</label>
												</div>

												<div className="dialog-actions one-column">
													{submitError && <span className="input-error">{submitError}</span>}
													<Button
														type="submit"
														className="button-confirm body-text"
														disabled={!acceptedConditions || !isFormValid || addUserProfileLoading || updateUserProfileLoading || (!(isEditMode && pendingEditProfile?.rights === 'Administratör') && !rolesComplete)}
													>
														{(isEditMode ? updateUserProfileLoading : addUserProfileLoading) ? (isEditMode ? 'Sparar...' : 'Lägger till...') : (isEditMode && isFormDirty ? 'Spara' : isEditMode ? 'Spara' : 'Lägg till')}
													</Button>

													<Button
														type="button"
														className="button-cancel body-text"
														onClick={(e) => {
															const dialog = (e.target as HTMLElement).closest('dialog');
															setIsEditMode(false);
															setPendingEditProfile(null);
															setIsFormDirty(false);
															setWasEditMode(false);
															dialog?.close();
														}
														}
													>
														Avbryt
													</Button>
												</div>
											</form>
										</>
									)}
								</div>
							</dialog>

							<RightsWrapper
								disableButtonText={'Lägg till användare'}
								disableButtonIcon={<PlusIcon width={12} height={12} />}
								disableButtonClasses="body-text disable-fill-color"
							>
								<Button className="button-edit body-text" onClick={() => {
									setIsEditMode(false);
									setPendingEditProfile(null);
									setFirstName('');
									setLastName('');
									setEmail('');
									setPhone('');
									setPersonalNumber('');
									setFirstNameError(undefined);
									setLastNameError(undefined);
									setEmailError(undefined);
									setPhoneError(undefined);
									setPersonalNumberError(undefined);
									setSubmitError(undefined);
									setSelectedRole('full');
									setAcceptedConditions(false);
									setIsFormDirty(false);
									dialogNewUserRef.current?.showModal();
								}}>
									<PlusIcon width={12} height={12} />
									Lägg till användare
								</Button>
							</RightsWrapper>
						</div>
						<div className="user-management-table-wrapper">
							<div className="user-management-table-wrapper__header has-dark-bg">
								<span className="user-info">Användare</span>
								<span className="user-email">E-post</span>
								<span className="user-phone">Telefonnummer</span>
								<span className="user-rights">Rättigheter</span>
								<span className="row-actions">Åtgärder</span>
							</div>

							<dialog ref={dialogRemoveUserRef} className="remove-user-dialog">
								<div className="dialog-content">
									<h2 className="title body-text">
										<span className="svg-icon-wrapper red round">
											<InfoIconOutlined width={28} height={28} />
										</span>
										Ta bort användare
									</h2>
									{pendingDeleteProfile && (
										<p className="delete-profile-info">
											<strong>{pendingDeleteProfile.firstName} {pendingDeleteProfile.lastName}</strong>
											<span>{pendingDeleteProfile.identifier}</span>
										</p>
									)}
									<p>Är du säker på att du vill ta bort den här användarens tillgång till kontot?</p>
									{deleteError && <span className="input-error">{deleteError}</span>}
									<div className="dialog-actions">
										<Button type="button" className="button-confirm body-text" onClick={handleDeleteUserConfirm} disabled={deleteUserProfileLoading}>
											{deleteUserProfileLoading ? 'Tar bort...' : 'Ta bort'}
										</Button>
										<Button type="button" className="button-cancel body-text" onClick={() => {
											setPendingDeleteProfile(null);
											setDeleteError(undefined);
											dialogRemoveUserRef.current?.close();
										}}>Avbryt</Button>
									</div>
								</div>
							</dialog>

							<div className="user-management-table-wrapper__list">
								{profilesWithRoles.map(profile => (
									<UserManagementTableRow
										key={profile.id}
										rowData={{
											name: `${profile.firstName} ${profile.lastName}`,
											email: profile.email,
											phone: profile.phoneNumber,
											rights: getRightsText(profile.roles),
											identifier: profile.identifier,
										}}
										onEdit={() => {
											setPendingEditProfile({ id: profile.id, firstName: profile.firstName, lastName: profile.lastName, email: profile.email, phone: profile.phoneNumber, identifier: profile.identifier, rights: getRightsText(profile.roles) });
											setFirstName(profile.firstName);
											setLastName(profile.lastName);
											setEmail(profile.email);
											setPhone(profile.phoneNumber);
											setPersonalNumber(profile.identifier);
											setSelectedRole(getRightsText(profile.roles) === 'Redaktör' ? 'full' : 'read');
											setAcceptedConditions(false);
											setIsFormDirty(false);
											setIsEditMode(true);
											dialogNewUserRef.current?.showModal();
										}}
										onDelete={() => {
											setPendingDeleteProfile({ id: profile.id, firstName: profile.firstName, lastName: profile.lastName, identifier: profile.identifier });
											dialogRemoveUserRef.current?.showModal();
										}}
									/>
								))}
							</div>
						</div>
					</div>

				</div>
			</div>
		</>
	)
}
export default UserManagementPage
